import { useEffect, useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Textarea,
} from "@chakra-ui/react";
import { format } from "date-fns";
import {
  ICompetenciaResumo,
  INegociacao,
  NegociacaoStatusEnum,
} from "@/models/consignado/proposal-billings.model";
import { renderCompetencia, renderCompetenciaFromDate } from "@/utils/consignado/renderCompetencia.utils";

// Formulário de negociação da CCB (seção "Negociação", HIS-024 / TAR-048).
// Criar e editar no mesmo modal: com `negociacao` preenche os campos e o botão
// vira "Salvar".
//  - Criação: competência obrigatória (POST exige `cobranca_competencia_id`),
//    escolhida entre as competências REAIS da cobrança (`competencias`).
//  - Edição: só data, status e descrição — o PUT /cobrancas/negociacoes/:id
//    não aceita `cobranca_competencia_id`, então a competência fica só
//    exibida (trocar apenas o rótulo desincronizaria do vínculo).

export type NegociacaoFormValues = {
  // Valor do <input type="datetime-local"> ("yyyy-MM-ddTHH:mm", hora local).
  data_contato: string;
  status: string;
  descricao: string;
  cobranca_competencia_id: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  // Presente = edição.
  negociacao?: INegociacao | null;
  competencias: ICompetenciaResumo[];
  competenciasCarregando: boolean;
  onSubmit: (values: NegociacaoFormValues) => Promise<unknown>;
  isSaving: boolean;
};

const paraDatetimeLocal = (data?: string) => {
  if (!data) return "";
  const d = new Date(data);
  return isNaN(d.getTime()) ? "" : format(d, "yyyy-MM-dd'T'HH:mm");
};

const valoresIniciais = (negociacao?: INegociacao | null): NegociacaoFormValues => ({
  data_contato: paraDatetimeLocal(negociacao?.data_contato),
  status: negociacao?.status ?? NegociacaoStatusEnum.PENDENTE,
  descricao: negociacao?.descricao ?? "",
  cobranca_competencia_id: negociacao?.cobranca_competencia_id ?? "",
});

export default function NegociacaoFormModal({
  isOpen,
  onClose,
  negociacao,
  competencias,
  competenciasCarregando,
  onSubmit,
  isSaving,
}: Props) {
  const [values, setValues] = useState<NegociacaoFormValues>(() => valoresIniciais(negociacao));
  const edicao = !!negociacao;

  useEffect(() => {
    if (isOpen) setValues(valoresIniciais(negociacao));
  }, [isOpen, negociacao]);

  // Na criação, pré-seleciona a 1ª competência assim que a lista chega.
  useEffect(() => {
    if (!isOpen || edicao || competencias.length === 0) return;
    setValues((prev) =>
      competencias.some((c) => c.competencia_id === prev.cobranca_competencia_id)
        ? prev
        : { ...prev, cobranca_competencia_id: competencias[0].competencia_id }
    );
  }, [isOpen, edicao, competencias]);

  const set = <K extends keyof NegociacaoFormValues>(campo: K, valor: NegociacaoFormValues[K]) =>
    setValues((prev) => ({ ...prev, [campo]: valor }));

  const invalido =
    !values.data_contato ||
    !values.descricao.trim() ||
    !values.status ||
    (!edicao && !values.cobranca_competencia_id);

  const handleSubmit = async () => {
    if (invalido) return;
    await onSubmit(values);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{edicao ? "Editar negociação" : "Adicionar nova negociação"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Flex flexDirection="column" gap={4}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Data do Contato</FormLabel>
              <Input
                type="datetime-local"
                value={values.data_contato}
                onChange={(e) => set("data_contato", e.target.value)}
                fontSize="sm"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Como foi a negociação</FormLabel>
              <Textarea
                value={values.descricao}
                onChange={(e) => set("descricao", e.target.value)}
                placeholder="Descreva como foi a negociação..."
                fontSize="sm"
                rows={4}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Status</FormLabel>
              <Select value={values.status} onChange={(e) => set("status", e.target.value)} fontSize="sm">
                <option value={NegociacaoStatusEnum.PENDENTE}>Pendente</option>
                <option value={NegociacaoStatusEnum.EM_ANDAMENTO}>Em andamento</option>
                <option value={NegociacaoStatusEnum.CONCLUIDA}>Concluída</option>
              </Select>
            </FormControl>

            {edicao ? (
              <FormControl>
                <FormLabel fontSize="sm">Competência (MM/YYYY)</FormLabel>
                <Input value={renderCompetencia(negociacao.competencia) || "—"} isReadOnly fontSize="sm" />
                <FormHelperText fontSize="xs">A competência não pode ser alterada.</FormHelperText>
              </FormControl>
            ) : (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Competência (MM/YYYY)</FormLabel>
                <Select
                  value={values.cobranca_competencia_id}
                  onChange={(e) => set("cobranca_competencia_id", e.target.value)}
                  fontSize="sm"
                  isDisabled={competenciasCarregando || competencias.length === 0}
                  placeholder={
                    competenciasCarregando
                      ? "Carregando competências..."
                      : competencias.length === 0
                        ? "Nenhuma competência disponível"
                        : undefined
                  }
                >
                  {competencias.map((competencia) => (
                    <option key={competencia.competencia_id} value={competencia.competencia_id}>
                      {renderCompetenciaFromDate(new Date(competencia.competencia))}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} size="sm" isDisabled={isSaving}>
            Cancelar
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleSubmit}
            size="sm"
            isDisabled={invalido}
            isLoading={isSaving}
          >
            {edicao ? "Salvar" : "Adicionar"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
